import React from "react";
import { FileText, ShoppingCart, TrendingUp, Clock } from "lucide-react";
import { motion } from "framer-motion";

const formatMoney = (value) =>
  `${Number(value || 0).toLocaleString("vi-VN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })} ₫`;

const InvoiceMiniDashboard = ({ summary }) => {
  const totalAmount = Number(summary?.total_amount || 0);
  const totalRecords = Number(summary?.total_records || 0);
  const purchaseCount = Number(summary?.purchase_count || 0);
  const purchaseAmount = Number(summary?.purchase_amount || 0);
  const saleCount = Number(summary?.sale_count || 0);
  const saleAmount = Number(summary?.sale_amount || 0);
  const pendingCount = Number(summary?.pending_processing_count || 0);
  const pendingAmount = Number(summary?.pending_processing_amount || 0);

  const items = [
    {
      label: "Tổng giá trị HĐ",
      subLabel: "Đang hiển thị",
      value: formatMoney(totalAmount),
      count: totalRecords,
      countLabel: "hóa đơn",
      icon: FileText,
      tone: {
        shell: "from-slate-800 via-slate-700 to-slate-900 shadow-slate-900/25",
        ring: "ring-white/10",
        motif: "text-white/15",
        soft: "text-slate-300",
        accent: "bg-white/10",
      },
    },
    {
      label: "Hóa đơn mua",
      subLabel: "Ghi nhận",
      value: formatMoney(purchaseAmount),
      count: purchaseCount,
      countLabel: "HĐ mua",
      icon: ShoppingCart,
      tone: {
        shell: "from-red-700 via-red-600 to-rose-800 shadow-red-900/30",
        ring: "ring-white/10",
        motif: "text-white/15",
        soft: "text-red-100/85",
        accent: "bg-white/10",
      },
    },
    {
      label: "Hóa đơn bán",
      subLabel: "Ghi nhận",
      value: formatMoney(saleAmount),
      count: saleCount,
      countLabel: "HĐ bán",
      icon: TrendingUp,
      tone: {
        shell:
          "from-emerald-700 via-emerald-600 to-teal-800 shadow-emerald-900/30",
        ring: "ring-white/10",
        motif: "text-white/15",
        soft: "text-emerald-100/85",
        accent: "bg-white/10",
      },
    },
    {
      label: "Chưa hoàn tất",
      subLabel: "Cần xử lý",
      value: formatMoney(pendingAmount),
      count: pendingCount,
      countLabel: "hóa đơn",
      icon: Clock,
      tone: {
        shell: "from-amber-600 via-amber-500 to-orange-700 shadow-amber-900/25",
        ring: "ring-white/10",
        motif: "text-white/20",
        soft: "text-amber-50/90",
        accent: "bg-white/10",
      },
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4 lg:gap-4 mb-4 md:mb-6">
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`relative min-w-0 overflow-hidden rounded-2xl bg-gradient-to-br ${item.tone.shell} p-3.5 sm:p-5 text-white shadow-lg ring-1 ${item.tone.ring}`}
          >
            {/* Họa tiết chìm */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden"
            >
              <div className="absolute -right-6 -top-8 h-28 w-28 rounded-full bg-white/5 blur-sm sm:h-36 sm:w-36" />
              <div className="absolute -bottom-10 -left-8 h-24 w-24 rounded-full bg-black/10 blur-md sm:h-32 sm:w-32" />
              <Icon
                strokeWidth={1.25}
                className={`absolute -right-2 -bottom-2 h-[4.5rem] w-[4.5rem] sm:h-28 sm:w-28 ${item.tone.motif} rotate-[-12deg]`}
              />
            </div>

            <div className="relative z-[1]">
              <p
                className={`text-[9px] sm:text-[10px] font-black uppercase tracking-[0.16em] ${item.tone.soft}`}
              >
                {item.subLabel}
              </p>
              <h3 className="mt-1 text-[11px] sm:text-sm font-bold leading-snug text-white/95">
                {item.label}
              </h3>

              <p className="mt-3 sm:mt-4 text-base sm:text-2xl font-black tabular-nums leading-tight tracking-tight break-all drop-shadow-sm">
                {item.value}
              </p>

              <div
                className={`mt-2.5 sm:mt-3 inline-flex items-center rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wide ${item.tone.accent} ${item.tone.soft}`}
              >
                {item.count} {item.countLabel}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default InvoiceMiniDashboard;
